'use client';

import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { Controller, FormProvider, useFormContext, useFormState } from 'react-hook-form';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { Input } from '..';

const Form = FormProvider;

const FormFieldContext = React.createContext({});

const FormField = ({ ...props }) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

const FormItemContext = React.createContext({});

function FormItem({ className, ...props }) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div data-slot="form-item" className={cn('relative grid gap-2', className)} {...props} />
    </FormItemContext.Provider>
  );
}

function FormLabel({ className, ...props }) {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={cn('data-[error=true]:text-destructive', className)}
      htmlFor={formItemId}
      {...props}
    />
  );
}

function FormControl({ ...props }) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  );
}

function FormDescription({ className, ...props }) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

function FormMessage({ className, ...props }) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? '') : props.children;

  if (!body) {
    return null;
  }

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn('text-destructive text-sm', className)}
      {...props}
    >
      {body}
    </p>
  );
}

/**
 * FormInputField
 *
 * @param {object} props
 * @param {object} props.form - React Hook Form instance
 * @param {string} props.name - Field name
 * @param {string|false} props.label - Optional label
 * @param {string} props.labelCss - Label className
 * @param {string} props.description - form description
 * @param {React.ComponentType} props.ChildRender - Optional component to render instead of Input
 * @example
 * // Default input
 * <FormInputField
 *   form={form}
 *   name="email"
 *   label="Email"
 *   placeholder="Enter your email"
 * />
 *
 * @example
 * // Custom component with ChildRender
 * <FormInputField
 *   form={form}
 *   name="is_system_role"
 *   label="System Role"
 *   ChildRender={({ value, onChange, ...rest }) => (
 *     <Switch checked={value} onCheckedChange={onChange} {...rest} />
 *   )}
 * />
 */
const FormInputField = ({
  form,
  className,
  name,
  label = false,
  labelCss,
  ChildRender,
  required = false,
  description,
  ...props
}) => {
  if (!name) {
    throw new Error("FormInputField requires a 'name' prop.");
  }

  if (!form) {
    throw new Error("FormInputField requires a 'form' prop.");
  }

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => {
        return (
          <FormItem className={cn('', className)}>
            {label && (
              <FormLabel className={cn('', labelCss)}>
                {required && <span className="text-destructive">*</span>}
                {label}
              </FormLabel>
            )}
            <FormControl>
              {ChildRender ? (
                <ChildRender field={field} fieldState={fieldState} {...props} />
              ) : (
                <Input {...field} {...props} />
              )}
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormInputField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField
};

